import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Languages, BookOpen, GraduationCap, Plus, Check, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";
import { LensCapture } from "@/components/LensCapture";
import { useI18n } from "@/context/I18nContext";

export default function TextLens() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [extract, setExtract] = useState(null);     // {text, readable, detected_language, is_target_language, profile}
  const [view, setView] = useState(null);           // {mode, data}
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState({});

  const onCapture = async (b64) => {
    setLoading(true); setExtract(null); setView(null);
    try {
      const r = await api.post("/lens/extract", { image: b64 });
      setExtract(r.data);
      if (!r.data.readable) toast.error(r.data.note || "Couldn't read that clearly — retake the photo.");
    } catch (e) {
      if (e?.response?.status === 402) toast.error("FluentLens is a premium feature.");
      else toast.error("Could not read that image. Try again.");
    } finally { setLoading(false); }
  };

  const analyze = async (mode) => {
    setBusy(true); setView(null);
    try {
      const r = await api.post("/lens/text/analyze", { text: extract.text, mode });
      setView({ mode, data: r.data });
    } catch { toast.error("Analysis failed — try again."); }
    finally { setBusy(false); }
  };

  const addVocab = async (items) => {
    const fresh = items.filter((v) => v.word && !saved[v.word]);
    if (!fresh.length) return;
    try {
      await api.post("/vocab/add", { items: fresh.map((v) => ({ word: v.word, translation: v.translation, example_sentence: v.note || extract?.text?.slice(0, 120) || "" })), source: "text_lens" });
      setSaved((s) => { const n = { ...s }; fresh.forEach((v) => { n[v.word] = true; }); return n; });
      toast.success(`Added ${fresh.length} to your Vocab Bank`);
    } catch { toast.error("Couldn't add to Vocab Bank."); }
  };

  const saveScan = async () => {
    try {
      await api.post("/lens/scans", { type: "text", extracted_text: extract.text, saved_vocab: Object.keys(saved) });
      toast.success("Saved to FluentLens Archive");
    } catch { toast.error("Couldn't save scan."); }
  };

  return (
    <AppLayout>
      <Seo title="Text Lens · FluentLens" />
      <div className="max-w-3xl mx-auto" data-testid="text-lens">
        <Link to="/lens" className="inline-flex items-center gap-1 text-sm font-bold text-gray-600 mb-3"><ArrowLeft className="w-4 h-4" /> FluentLens</Link>
        <h1 className="font-cabinet font-extrabold text-3xl mb-1">{t("lens.textLens")}</h1>
        <p className="text-gray-600 text-sm mb-5">{t("lensDesc.subText")}</p>

        {!extract && <LensCapture onCapture={onCapture} loading={loading} hint={t("lensDesc.hintText")} />}

        {extract && (
          <div className="space-y-4">
            <div className="nb-card p-4" data-testid="extract-preview">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-bold uppercase text-gray-500">Extracted text {extract.detected_language ? `· ${extract.detected_language}` : ""}</div>
                <button onClick={() => { setExtract(null); setView(null); setSaved({}); }} className="text-xs underline text-gray-500" data-testid="text-retake">{t("common.retake")}</button>
              </div>
              {extract.readable
                ? <p className="whitespace-pre-wrap text-sm" dir="auto">{extract.text}</p>
                : <p className="text-sm text-gray-500">{extract.note || "Text was unreadable. Please retake the photo."}</p>}
            </div>

            {extract.readable && (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <button onClick={() => analyze("translate")} disabled={busy} className="nb-btn-secondary py-3 text-sm" data-testid="text-translate"><Languages className="w-4 h-4" /> {t("lens.translate")}</button>
                  <button onClick={() => analyze("explain")} disabled={busy} className="nb-btn-primary py-3 text-sm" data-testid="text-explain"><BookOpen className="w-4 h-4" /> {t("lens.explain")}</button>
                  <button onClick={() => navigate("/lens/study", { state: { text: extract.text } })} className="nb-btn-outline py-3 text-sm" data-testid="text-quiz"><GraduationCap className="w-4 h-4" /> {t("lens.quizMe")}</button>
                </div>

                {busy && <div className="text-center text-gray-400 py-4"><RefreshCw className="w-6 h-6 animate-spin mx-auto" /></div>}

                {view?.mode === "translate" && (
                  <div className="nb-card bg-mint/40 p-4" data-testid="text-translation">
                    <div className="text-xs font-bold uppercase text-gray-500 mb-1">{t("label.naturalTranslation")}</div>
                    <p className="whitespace-pre-wrap" dir="auto">{view.data.translation}</p>
                  </div>
                )}

                {view?.mode === "explain" && (
                  <div className="space-y-3" data-testid="text-explanation">
                    <div className="nb-card bg-sky/40 p-4">
                      <div className="flex items-center justify-between"><div className="text-xs font-bold uppercase text-gray-500">{t("label.gist")}</div>{view.data.cefr && <span className="nb-tag bg-white text-xs">{view.data.cefr}</span>}</div>
                      <p className="mt-1">{view.data.summary}</p>
                    </div>
                    {(view.data.vocabulary || []).length > 0 && (
                      <div className="nb-card p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-cabinet font-bold">Useful vocabulary</div>
                          <button onClick={() => addVocab(view.data.vocabulary)} className="text-xs font-bold text-coral" data-testid="text-add-all">Add all useful words</button>
                        </div>
                        <ul className="space-y-1 text-sm">
                          {view.data.vocabulary.map((v, i) => (
                            <li key={`${v.word}-${i}`} className="flex items-center justify-between gap-2">
                              <span><b dir="auto">{v.word}</b> — <span className="text-gray-600" dir="auto">{v.translation}</span>{v.note ? <span className="text-gray-400" dir="auto"> · {v.note}</span> : ""}</span>
                              {saved[v.word] ? <span className="text-xs text-gray-400 flex items-center gap-0.5"><Check className="w-3 h-3" /> Added</span>
                                : <button onClick={() => addVocab([v])} className="text-xs font-bold text-coral shrink-0" data-testid={`text-add-${i}`}><Plus className="w-3 h-3 inline" /> Add</button>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(view.data.phrases || []).length > 0 && (
                      <div className="nb-card bg-peach/30 p-4"><div className="font-cabinet font-bold mb-1">{t("label.usefulPhrases")}</div>
                        <ul className="space-y-1 text-sm">{view.data.phrases.map((p, i) => <li key={`${p.phrase}-${i}`}><b dir="auto">{p.phrase}</b> — <span dir="auto">{p.explanation}</span></li>)}</ul></div>
                    )}
                    {(view.data.grammar || []).length > 0 && (
                      <div className="nb-card bg-lavender/30 p-4"><div className="font-cabinet font-bold mb-1">{t("label.grammar")}</div>
                        <ul className="space-y-1 text-sm">{view.data.grammar.map((g, i) => <li key={`${g.point}-${i}`}><b dir="auto">{g.point}</b> — <span dir="auto">{g.explanation}</span></li>)}</ul></div>
                    )}
                    {(view.data.idioms || []).length > 0 && (
                      <div className="nb-card bg-butter/40 p-4"><div className="font-cabinet font-bold mb-1">{t("label.idioms")}</div>
                        <ul className="space-y-1 text-sm">{view.data.idioms.map((x, i) => <li key={`${x.expression}-${i}`}><b dir="auto">{x.expression}</b> — <span dir="auto">{x.meaning}</span></li>)}</ul></div>
                    )}
                  </div>
                )}

                <button onClick={saveScan} className="nb-btn-outline w-full py-3" data-testid="text-save-scan"><Save className="w-4 h-4" /> {t("lens.saveScan")}</button>
              </>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
